(() => {
  const q = (selector, scope = document) => scope.querySelector(selector);
  const qa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const config = window.raTheme || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const forbiddenSupplierAssetPattern = /(color-style-reference|finish-fit-reference|gold-silver-reference|pattern-options|floral-color-options|pearl-style-options|velvet-color-options)/i;
  const isForbiddenSupplierAsset = (value) => forbiddenSupplierAssetPattern.test(String(value || ''));

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const formatMoney = (cents) => {
    const currency = config.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const setMessage = (form, message, isError = false) => {
    const output = q('[data-ra-form-message], [data-ra-form-status]', form);
    if (!output) return;
    output.textContent = message;
    output.classList.toggle('is-error', isError);
  };

  const cartDrawer = q('[data-ra-cart-drawer]');
  const cartScrim = q('[data-ra-cart-scrim]');
  let returnFocus = null;

  const setCartCount = (count) => {
    qa('[data-ra-cart-count]').forEach((node) => {
      node.textContent = count;
      node.setAttribute('aria-label', `${count} items`);
    });
    qa('[data-ra-bag-open]').forEach((link) => {
      link.setAttribute('aria-label', `Shopping bag, ${count} ${count === 1 ? 'item' : 'items'}`);
    });
  };

  const renderCart = (cart) => {
    setCartCount(cart.item_count);
    const list = q('[data-ra-cart-items]');
    const empty = q('[data-ra-cart-empty]');
    const summary = q('[data-ra-cart-summary]');
    if (!list || !empty || !summary) return;

    list.textContent = '';
    empty.hidden = cart.item_count > 0;
    summary.hidden = cart.item_count === 0;

    cart.items.forEach((item, index) => {
      const article = document.createElement('article');
      article.className = 'ra-cart-item';
      const candidateImage = item.featured_image?.url || item.image;
      const image = isForbiddenSupplierAsset(candidateImage) ? '' : candidateImage;
      const properties = Object.entries(item.properties || {})
        .filter(([key, value]) => value && !key.startsWith('_'))
        .map(([key, value]) => {
          const displayValue = key === 'Reference photo' && /^https?:\/\//i.test(String(value))
            ? 'Uploaded'
            : value;
          return `<span>${escapeHtml(key)}: ${escapeHtml(displayValue)}</span>`;
        })
        .join('');
      article.innerHTML = `
        ${image ? `<a class="ra-cart-item__image" href="${escapeHtml(item.url)}"><img src="${escapeHtml(image)}&width=240" alt="${escapeHtml(item.product_title)}" width="120" height="150"></a>` : ''}
        <div class="ra-cart-item__copy">
          <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${item.variant_title ? `<p>${escapeHtml(item.variant_title)}</p>` : ''}
          ${properties ? `<div class="ra-cart-item__properties">${properties}</div>` : ''}
          <strong>${formatMoney(item.final_line_price)}</strong>
          <div class="ra-quantity" aria-label="Quantity">
            <button type="button" data-ra-cart-change data-line="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">&minus;</button>
            <span>${item.quantity}</span>
            <button type="button" data-ra-cart-change data-line="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
        </div>`;
      list.append(article);
    });

    const total = q('[data-ra-cart-total]');
    if (total) total.textContent = `${formatMoney(cart.total_price)} ${cart.currency || config.currency || ''}`.trim();
  };

  const refreshCart = async () => {
    const response = await fetch(`${config.cartUrl || '/cart'}.js?ra=${Date.now()}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('The bag could not be refreshed.');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const openCart = async (trigger) => {
    if (!cartDrawer) return;
    returnFocus = trigger || document.activeElement;

    // Fetch the current cart before revealing the drawer. The previous order
    // showed the server-rendered empty state while /cart.js was still loading,
    // which made the checkout CTA appear to be missing after a successful add.
    try {
      await refreshCart();
    } catch (error) {
      // Never leave a successful add behind an empty drawer with no checkout
      // path. The full cart template is the reliable fallback.
      window.location.assign(config.cartUrl || '/cart');
      return;
    }

    cartDrawer.hidden = false;
    if (cartScrim) cartScrim.hidden = false;
    requestAnimationFrame(() => {
      cartDrawer.classList.add('is-open');
      cartScrim?.classList.add('is-open');
    });
    document.body.classList.add('ra-cart-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    q('[data-ra-cart-close]', cartDrawer)?.focus();
  };

  const closeCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartScrim?.classList.remove('is-open');
    document.body.classList.remove('ra-cart-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => {
      cartDrawer.hidden = true;
      if (cartScrim) cartScrim.hidden = true;
    }, reducedMotion ? 0 : 360);
    returnFocus?.focus?.();
  };

  document.addEventListener('click', async (event) => {
    const opener = event.target.closest('[data-ra-bag-open]');
    if (opener) {
      event.preventDefault();
      openCart(opener);
      return;
    }
    if (event.target.closest('[data-ra-cart-close]') || event.target === cartScrim) {
      closeCart();
      return;
    }
    const quantityButton = event.target.closest('[data-ra-cart-change]');
    if (quantityButton) {
      quantityButton.disabled = true;
      try {
        const response = await fetch(`${config.cartChangeUrl || '/cart/change'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
          body: JSON.stringify({ line: Number(quantityButton.dataset.line), quantity: Number(quantityButton.dataset.quantity) })
        });
        if (!response.ok) throw new Error('Quantity could not be updated.');
        renderCart(await response.json());
      } catch (error) {
        quantityButton.disabled = false;
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeCart();
    if (event.key === 'Tab' && cartDrawer?.classList.contains('is-open')) {
      const focusable = qa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])', cartDrawer);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });

  const addProduct = async (form) => {
    const button = q('[type="submit"]', form);
    const originalLabel = button?.textContent;
    if (button) { button.disabled = true; button.textContent = 'Adding…'; }
    setMessage(form, '');
    try {
      const response = await fetch(`${config.cartAddUrl || '/cart/add'}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'This item could not be added.');
      if (button) button.textContent = 'Added to bag';
      setMessage(form, 'Added to your bag.');
      await openCart(button);
    } catch (error) {
      setMessage(form, error.message, true);
      if (button) { button.disabled = false; button.textContent = originalLabel; }
      return;
    }
    window.setTimeout(() => {
      if (button) { button.disabled = false; button.textContent = originalLabel; }
    }, 1200);
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-ra-home-product-form], [data-ra-product-form]');
    if (!form) return;
    event.preventDefault();
    addProduct(form);
  });

  const initHeader = () => {
    const header = q('[data-ra-header]');
    const mobileMenu = q('[data-ra-mobile-menu]');
    const mobileMenuToggle = mobileMenu ? q('.ra-mobile-menu__toggle', mobileMenu) : null;
    document.body.classList.toggle('ra-has-announcement', Boolean(q('.ra-announcement', header?.parentElement || document)));
    if (header) {
      const paint = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
      paint();
      window.addEventListener('scroll', paint, { passive: true });
    }
    const syncMobileMenuState = () => {
      const isOpen = Boolean(mobileMenu?.open);
      document.body.classList.toggle('ra-menu-open', isOpen);
      mobileMenuToggle?.setAttribute('aria-expanded', String(isOpen));
    };
    mobileMenu?.addEventListener('toggle', syncMobileMenuState);
    syncMobileMenuState();
    mobileMenu?.addEventListener('click', (event) => {
      if (!event.target.closest('a')) return;
      mobileMenu.open = false;
      document.body.classList.remove('ra-menu-open');
    });
  };

  const initHome = (root) => {
    const html = document.documentElement;
    let ticking = false;
    const paint = () => {
      const y = window.scrollY;
      const max = Math.max(document.body.scrollHeight - window.innerHeight, 1);
      html.style.setProperty('--page-progress', String(Math.min(y / max, 1)));
      if (!reducedMotion) {
        html.style.setProperty('--hero-shift', `${Math.min(y * 0.18, 130)}px`);
        html.style.setProperty('--hero-copy-shift', `${Math.min(y * -0.08, 0)}px`);
        html.style.setProperty('--hero-scale', String(Math.min(y / 9000, 0.055)));
        html.style.setProperty('--ribbon-shift', `${Math.max(-36, Math.min(36, (y - 700) * -0.018))}px`);
      }
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(paint);
    }, { passive: true });
    paint();

    const sections = qa('[data-scene]', root);
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      [...root.classList].filter((name) => name.startsWith('active-')).forEach((name) => root.classList.remove(name));
      root.classList.add(`active-${visible.target.dataset.scene}`);
    }, { threshold: [0.2, 0.45, 0.7], rootMargin: '-12% 0px -18%' });
    sections.forEach((section) => observer.observe(section));

    const moodSection = q('.mood-section', root);
    const moodButtons = qa('[data-ra-mood]', root);
    const activateMood = (button) => {
      const mood = button.dataset.raMood;
      moodButtons.forEach((item) => {
        const selected = item === button;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-checked', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      qa('[data-ra-mood-image]', root).forEach((image) => image.classList.toggle('is-active', image.dataset.raMoodImage === mood));
      moodSection.className = `mood-section mood-${mood}`;
      const note = q('[data-ra-mood-note]', root);
      if (note) note.textContent = button.dataset.note;
      root.classList.toggle('header-dark', mood === 'midnight');
    };
    moodButtons.forEach((button, index) => {
      button.addEventListener('click', () => activateMood(button));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(event.key)) return;
        event.preventDefault();
        const delta = ['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1;
        const next = moodButtons[(index + delta + moodButtons.length) % moodButtons.length];
        next.focus();
        activateMood(next);
      });
    });

    const productData = q('[data-ra-home-product-json]', root);
    const variants = productData ? JSON.parse(productData.textContent) : [];
    const optionSelects = qa('[data-ra-home-option]', root);
    const groups = new Map();
    optionSelects.forEach((select) => {
      const index = select.dataset.optionIndex;
      if (!groups.has(index)) groups.set(index, []);
      groups.get(index).push(select);
    });
    const syncVariant = (changed) => {
      const selected = [];
      groups.forEach((selects, index) => {
        if (changed) {
          const matching = selects.find((select) => select.dataset.optionIndex === changed.dataset.optionIndex);
          if (matching) selects.forEach((select) => { if (select !== changed) select.value = changed.value; });
        }
        selected[Number(index)] = selects[0]?.value;
      });
      const variant = variants.find((item) => item.options.every((option, index) => option === selected[index]));
      if (!variant) return;
      qa('[data-ra-home-variant-id]', root).forEach((input) => { input.value = variant.id; });
      const price = q('[data-ra-home-price]', root);
      if (price) price.textContent = formatMoney(variant.price);
      const title = q('[data-ra-home-variant-title]', root);
      if (title) title.textContent = variant.title;
      const button = q('[data-ra-add-label]', root);
      if (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available ? 'Add to bag' : 'Sold out';
      }
    };
    optionSelects.forEach((select) => select.addEventListener('change', () => syncVariant(select)));
    syncVariant();
    q('[data-ra-home-config-form]', root)?.addEventListener('submit', (event) => {
      event.preventDefault();
      q('.product-section', root)?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' });
    });
  };

  const initInnerPages = () => {
    qa('.ra-product-description img').forEach((image) => {
      if (!isForbiddenSupplierAsset(image.currentSrc || image.src)) return;
      const paragraph = image.closest('p');
      const heading = paragraph?.previousElementSibling;
      paragraph?.remove();
      if (heading?.matches('h2, h3, h4') && /(?:color|finish|fit|pattern|style)\s*(?:&|and)?\s*guide/i.test(heading.textContent)) {
        heading.remove();
      }
    });

    qa('[data-ra-gallery-thumb], [data-ra-media-id]').forEach((item) => {
      const image = q('img', item);
      if (image && isForbiddenSupplierAsset(image.currentSrc || image.src)) item.remove();
    });

    const reveal = qa('[data-ra-reveal]');
    if (reveal.length) {
      const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); }
      }), { threshold: 0.12 });
      reveal.forEach((node) => observer.observe(node));
    }

    qa('[data-ra-variant-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const form = select.closest('[data-ra-product-form]');
        const option = select.selectedOptions[0];
        const idInput = q('[name="id"]', form);
        if (idInput) idInput.value = select.value;
        const price = q('[data-ra-product-price]', form?.closest('.ra-product') || document);
        if (price && option.dataset.priceFormatted) price.innerHTML = `<span>${escapeHtml(option.dataset.priceFormatted)}</span>${option.dataset.comparePriceFormatted ? `<s>${escapeHtml(option.dataset.comparePriceFormatted)}</s>` : ''}`;
        const submit = q('[type="submit"]', form);
        if (submit) {
          const available = option.disabled !== true && option.dataset.available !== 'false';
          submit.disabled = !available;
          submit.textContent = available ? 'Add to bag' : 'Sold out';
        }
        if (option.dataset.mediaId) {
          const productPage = form?.closest('[data-ra-product-page]');
          const gallery = q('[data-ra-gallery]', productPage || document);
          const mediaThumb = qa('[data-ra-gallery-thumb]', gallery).find(
            (item) => item.dataset.raGalleryThumb === option.dataset.mediaId,
          );
          if (mediaThumb) mediaThumb.click();
        }
      });
    });

    qa('[data-ra-file-input]').forEach((input) => input.addEventListener('change', () => {
      const name = q('[data-ra-file-name]', input.closest('label') || input.parentElement);
      if (name) name.textContent = input.files?.[0]?.name || 'Choose a photograph';
    }));

    qa('[data-ra-gallery]').forEach((gallery) => {
      qa('[data-ra-gallery-thumb]', gallery).forEach((button) => button.addEventListener('click', () => {
        const mediaId = button.dataset.raGalleryThumb;
        qa('[data-ra-gallery-thumb]', gallery).forEach((item) => {
          const selected = item === button;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-pressed', String(selected));
        });
        qa('[data-ra-media-id]', gallery).forEach((media) => {
          const selected = media.dataset.raMediaId === mediaId;
          media.classList.toggle('is-active', selected);
          media.setAttribute('aria-hidden', String(!selected));
        });
      }));
    });

    const faqList = q('[data-ra-faq-list]');
    if (faqList) {
      let activeCategory = 'all';
      let searchTerm = '';
      const items = qa('.ra-faq-item', faqList);
      const updateFaq = () => {
        let visible = 0;
        items.forEach((item) => {
          const matchesCategory = activeCategory === 'all' || item.dataset.category === activeCategory;
          const matchesSearch = !searchTerm || item.textContent.toLowerCase().includes(searchTerm);
          item.hidden = !(matchesCategory && matchesSearch);
          if (!item.hidden) visible += 1;
        });
        const empty = q('[data-ra-faq-empty]');
        if (empty) empty.hidden = visible > 0;
        const status = q('[data-ra-faq-status]');
        if (status) status.textContent = `${visible} ${visible === 1 ? 'answer' : 'answers'} shown.`;
      };
      q('[data-ra-faq-search]')?.addEventListener('input', (event) => { searchTerm = event.target.value.trim().toLowerCase(); updateFaq(); });
      qa('[data-ra-faq-filter]').forEach((button) => button.addEventListener('click', () => {
        activeCategory = button.dataset.raFaqFilter || button.dataset.category || 'all';
        qa('[data-ra-faq-filter]').forEach((item) => {
          const selected = item === button;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-pressed', String(selected));
        });
        updateFaq();
      }));
      updateFaq();
    }
  };

  initHeader();
  qa('[data-ra-home]').forEach(initHome);
  initInnerPages();
  refreshCart().catch(() => {});
})();
